import React from 'react';

import {
  STORE_RECENT_ADDRESS_TITLE_WITH_OPTIONS,
  STORE_RECENT_ADDRESS_TITLE_WITHOUT_OPTIONS,
} from '../constants/storeCheckout.constants';
import { getRecentAddressAriaLabel } from '../helpers/storeRecentAddress.utils';
import type { IRecentAddressSuggestion } from '@/common/interfaces/mostRecent.interface';

import './StoreRecentAddress.scss';

interface IStoreRecentAddressProps {
  recentAddresses: IRecentAddressSuggestion[];
  selectedAddressId: number | null;
  onAddressSelect: (address: IRecentAddressSuggestion) => void;
  isReadOnly?: boolean;
}

const StoreRecentAddress: React.FC<IStoreRecentAddressProps> = ({
  recentAddresses,
  selectedAddressId,
  onAddressSelect,
  isReadOnly = false,
}) => {
  const styleClass = 'store-recent-address';
  const hasRecentAddresses = recentAddresses.length > 0;

  return (
    <div className={`${styleClass}__container`} data-testid="store-recent-address-section">
      <div className={`${styleClass}__title`}>
        {hasRecentAddresses
          ? STORE_RECENT_ADDRESS_TITLE_WITH_OPTIONS
          : STORE_RECENT_ADDRESS_TITLE_WITHOUT_OPTIONS}
      </div>

      {hasRecentAddresses && (
        <div className={`${styleClass} smooth-animation`}>
          <div className={`${styleClass}__list`} data-testid="store-recent-address-list">
            {recentAddresses.map((address) => {
              const isSelected = selectedAddressId === address.id;

              return (
                <div
                  key={address.id}
                  className={`${styleClass}__card ${
                    isSelected ? `${styleClass}__card--selected` : ''
                  } ${isReadOnly ? `${styleClass}__card--disabled` : ''}`}
                  onClick={() => onAddressSelect(address)}
                  onKeyDown={(event) => {
                    if (!isReadOnly && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      onAddressSelect(address);
                    }
                  }}
                  role="button"
                  tabIndex={isReadOnly ? -1 : 0}
                  aria-pressed={isSelected}
                  aria-label={getRecentAddressAriaLabel(address)}
                  aria-disabled={isReadOnly}
                  data-testid={`store-recent-address-${address.id}`}
                >
                  <div className={`${styleClass}__checkbox`}>
                    <input
                      type="checkbox"
                      className={`${styleClass}__checkbox-input`}
                      checked={isSelected}
                      readOnly
                      tabIndex={-1}
                      aria-hidden="true"
                    />
                  </div>

                  <div className={`${styleClass}__content`}>
                    <div className={`${styleClass}__street`}>
                      {address.street1}
                      {address.street2 ? `, ${address.street2}` : ''}
                    </div>
                    <div className={`${styleClass}__street`}>
                      {address.city}, {address.state} {address.zipCode}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

StoreRecentAddress.displayName = 'StoreRecentAddress';

export default StoreRecentAddress;
